import { ArrowUpRight, ExternalLink } from 'lucide-react';
import type { MouseEvent, ReactNode } from 'react';
import type { BookingKind } from './dispatchLink';
import { useJobTarget } from './jobTarget';

/**
 * A job number that opens the job in the dispatch dashboard — the one way the
 * CRM draws a booking's number, wherever it shows one: the contact panel's
 * bookings, the booked-from-this-thread strip, a duplicate the booking draft
 * found, the mail layout's last reservation, and the note a booking leaves in
 * its thread.
 *
 * Three ways, in the order of how well each lands — ContactPanel's ProfileLink
 * is the same rule for a person:
 *
 *   1. Framed by a dashboard that handles OPEN_JOB (`source.openJob`): a button
 *      that asks the DASHBOARD to open it. No second tab, no second sign-in;
 *      the dashboard's own router and permission decide what this agent sees,
 *      and the conversation stays where it was.
 *   2. Otherwise, wherever a dashboard address is known (the parent frame, a
 *      build's override, the tenant's own dashboard from /meta): a link to the
 *      job's route, in a new tab.
 *   3. Neither: the number as text, which still reads and still copies — and
 *      carries `is-plain`, so a spot that colours its links can tell.
 *
 * Every press stops here. A number sits inside things that open something
 * else when clicked — a mail list row opens its thread — and pressing the
 * number must open the job, not the row it is printed on.
 *
 * The decision itself is `useJobTarget` (jobTarget.ts), shared with the one row
 * that cannot hold a control of its own and presses the number by delegation.
 */
export function JobLink({
  kind,
  id,
  children,
  className = '',
  title,
  icon = false,
  plain = 'text',
}: {
  kind: BookingKind;
  /** Dispatch's own id: a job id for a job or a quote, a draft row id for a draft. */
  id: string | number | null | undefined;
  children: ReactNode;
  className?: string;
  title?: string;
  /**
   * A mark after the number saying where a press goes: ↗ for the dashboard
   * around this frame, the new-tab mark for a link out. Never on plain text.
   */
  icon?: boolean;
  /**
   * What to draw when there is nowhere to open it: the number as text, or —
   * for a control that is ONLY a way out, like a row's "Open" — nothing, since
   * an "Open" that opens nothing is a dead control.
   */
  plain?: 'text' | 'none';
}) {
  const { host, href } = useJobTarget(kind, id);
  const noun = kind === 'draft' ? 'draft' : kind === 'quote' ? 'quote' : 'job';
  const classes = `job-link${className ? ` ${className}` : ''}`;

  // The host refuses an id it cannot address exactly, so only a plain number
  // becomes a button — the rule `recordHref` has for a link.
  if (host) {
    return (
      <button
        type="button"
        className={classes}
        title={title ?? `Open this ${noun} in the dashboard`}
        onClick={(event: MouseEvent) => {
          event.stopPropagation();
          host();
        }}
      >
        {children}
        {icon && <ArrowUpRight size={11} />}
      </button>
    );
  }
  if (href) {
    return (
      <a
        className={classes}
        href={href}
        target="_blank"
        rel="noreferrer"
        title={title ?? `Open this ${noun} in the dispatch dashboard`}
        onClick={(event: MouseEvent) => event.stopPropagation()}
      >
        {children}
        {icon && <ExternalLink size={10} />}
      </a>
    );
  }
  if (plain === 'none') return null;
  return (
    <span className={`${className} is-plain`.trim()} title={title}>
      {children}
    </span>
  );
}
